/**
 * Art mix for coins (realism pass v2 §5 / B §4). The spec's ~50% "AI meme image" share is limited by the 16 approved
 * tiles available here (no image generator in this environment); the remainder uses crude procedural paint, text logos
 * and the attributed sticker library. See docs/ASSETS.md.
 */
import type { Rng } from './rng';
import type { Art } from './types';
import { CONCEPTS } from './catalog';

export const PHOTO_TILES = ['frog-meh', 'scruffy-dog', 'rubber-duck', 'sweater-dog', 'grumpy-cat', 'egg-keyboard', 'frog-wallet', 'capybara', 'doodle-robot', 'traffic-cone', 'sad-blob', 'bin-raccoon', 'plastic-chair', 'paper-rocket', 'king-worm', 'wild-mushroom'];
const TILE_TAG: Record<string, string[]> = { frog: ['frog-meh', 'frog-wallet'], dog: ['scruffy-dog', 'sweater-dog'], 'duck-toy': ['rubber-duck'], duck: ['rubber-duck'], cat: ['grumpy-cat'], egg: ['egg-keyboard'], capybara: ['capybara'], robot: ['doodle-robot'], cone: ['traffic-cone'], mascot: ['sad-blob'], raccoon: ['bin-raccoon'], chair: ['plastic-chair'], rocket: ['paper-rocket'], snake: ['king-worm'], mushroom: ['wild-mushroom'] };
export const tileFor = (tag: string, r: Rng) => r.pick(TILE_TAG[tag] ?? PHOTO_TILES);

export function noiseArt(r: Rng, ticker: string, tag: string): Art {
  const seed = r.int(1, 2 ** 31); const k = r.next();
  if (k < 0.2) return { seed, kind: 'photo', photo: tileFor(tag, r), fx: { zoom: r.chance(0.5) ? r.range(1.1, 1.6) : undefined, mirror: r.chance(0.3), hue: r.chance(0.3) ? r.int(20, 300) : undefined, fried: r.chance(0.2) } };
  if (k < 0.65) return { seed, kind: 'paint', tag, text: r.chance(0.35) ? ticker : undefined };
  if (k < 0.9) return { seed, kind: 'text', text: ticker };
  return { seed, concept: CONCEPTS[Math.floor(r.next() * CONCEPTS.length)]!.id };
}

/** Copycat image variants of a canonical post image (B §3): exact 40, crop/zoom 20, mirror/hue 10, tweet screenshot 15, unrelated 15. */
export function copyArt(r: Rng, orig: Art, postText: string, author: string, ticker: string): Art {
  const v = r.weighted<string>([['exact', 40], ['crop', 20], ['mirror', 10], ['shot', 15], ['other', 15]]);
  if (v === 'exact') return { ...orig };
  if (v === 'crop') return { ...orig, crop: r.int(1, 3), fx: { ...orig.fx, zoom: r.range(1.2, 1.8) } };
  if (v === 'mirror') return { ...orig, fx: { ...orig.fx, mirror: r.chance(0.6), hue: r.int(25, 330) } };
  if (v === 'shot') return { seed: r.int(1, 2 ** 31), kind: 'tweet', text: postText, tag: author };
  return noiseArt(r, ticker, orig.tag ?? '');
}
